import { useMemo } from "react";
import { t } from "ttag";

import { skipToken, useListCollectionsTreeQuery } from "metabase/api";
import { ROOT_COLLECTION } from "metabase/common/collections/constants";
import getExpandedCollectionsById from "metabase/common/collections/getExpandedCollectionsById";
import { useSetting } from "metabase/common/hooks/use-setting";
import { PLUGIN_TENANTS } from "metabase/plugins";
import { useSelector } from "metabase/redux";
import { getUserPersonalCollectionId } from "metabase/selectors/user";
import type { Collection, CollectionId } from "metabase-types/api";

// getExpandedCollectionsById produces path as CollectionId[] at runtime,
// but ExpandedCollection types it as string, so we use a corrected type.
export type ExpandedCollectionNode = Collection & {
  path: CollectionId[] | null;
  parent: Collection | null;
  children: Collection[];
  is_personal?: boolean;
};

export const SHARED_TENANT_COLLECTIONS_ROOT_ID =
  "shared-tenant-collections-root" as CollectionId;

export const COLLECTIONS_TOP_LEVEL_ID = "collections-top-level" as CollectionId;

/**
 * When tenants are enabled, fetches shared tenant collections and merges them
 * into the collectionsById map so they appear as a top-level browsable entry.
 *
 * The tree structure becomes:
 *   Collections (top level)
 *   ├── Our analytics (root collection)
 *   └── Shared collections (synthetic root for shared collections)
 *       ├── Shared collection A
 *       └── Shared collection B
 */
export function useCollectionsWithTenants(
  collectionsById: Record<CollectionId, Collection>,
): Record<CollectionId, Collection> {
  const useTenants = useSetting("use-tenants");
  const userPersonalCollectionId = useSelector(getUserPersonalCollectionId);
  const isTenantsActive = useTenants && PLUGIN_TENANTS.isEnabled;

  const { data: sharedTenantCollections } = useListCollectionsTreeQuery(
    isTenantsActive
      ? {
          namespace: PLUGIN_TENANTS.SHARED_TENANT_NAMESPACE,
          "exclude-archived": true,
        }
      : skipToken,
  );

  return useMemo(() => {
    if (!isTenantsActive || !sharedTenantCollections?.length) {
      return collectionsById;
    }

    const sharedCollectionsById = getExpandedCollectionsById(
      sharedTenantCollections,
      userPersonalCollectionId,
    ) as Record<CollectionId, ExpandedCollectionNode>;

    const displayName =
      PLUGIN_TENANTS.getNamespaceDisplayName(
        PLUGIN_TENANTS.SHARED_TENANT_NAMESPACE,
      ) ?? "";

    return mergeSharedCollections(
      collectionsById,
      sharedCollectionsById,
      displayName,
    );
  }, [
    isTenantsActive,
    sharedTenantCollections,
    collectionsById,
    userPersonalCollectionId,
  ]);
}

/**
 * Merge shared tenant collections into the base collections map,
 * creating a top-level "Collections" node that contains both
 * "Our analytics" and "Shared collections" as siblings.
 */
export function mergeSharedCollections(
  baseCollectionsById: Record<CollectionId, Collection>,
  sharedCollectionsById: Record<CollectionId, ExpandedCollectionNode>,
  displayName: string,
): Record<CollectionId, Collection> {
  const sharedRoot = sharedCollectionsById[ROOT_COLLECTION.id];
  const rootCollection = baseCollectionsById[
    ROOT_COLLECTION.id
  ] as ExpandedCollectionNode;

  // Create the top-level "Collections" node that parents both namespaces
  const syntheticTopLevel: ExpandedCollectionNode = {
    id: COLLECTIONS_TOP_LEVEL_ID,
    name: t`Collections`,
    description: null,
    can_write: false,
    can_restore: false,
    can_delete: false,
    namespace: null,
    location: null,
    path: [],
    parent: null,
    children: [],
  };

  // Create the shared collections synthetic root as a sibling of Our analytics
  const sharedSyntheticRoot: ExpandedCollectionNode = {
    id: SHARED_TENANT_COLLECTIONS_ROOT_ID,
    name: displayName,
    description: null,
    can_write: false,
    can_restore: false,
    can_delete: false,
    namespace: PLUGIN_TENANTS.SHARED_TENANT_NAMESPACE,
    location: null,
    path: [COLLECTIONS_TOP_LEVEL_ID],
    parent: syntheticTopLevel,
    children: (sharedRoot?.children ?? []).map((child) => ({
      ...child,
      parent: null,
    })) as ExpandedCollectionNode[],
  };

  // Fix circular parent reference now that sharedSyntheticRoot exists
  sharedSyntheticRoot.children = sharedSyntheticRoot.children.map((child) => ({
    ...child,
    parent: sharedSyntheticRoot,
  })) as ExpandedCollectionNode[];

  // Wire up the top-level children
  syntheticTopLevel.children = [rootCollection, sharedSyntheticRoot];

  const mergedCollectionsById = { ...baseCollectionsById };

  // Rewrite root collection to point to top-level parent
  mergedCollectionsById[ROOT_COLLECTION.id] = {
    ...rootCollection,
    path: [COLLECTIONS_TOP_LEVEL_ID],
    parent: syntheticTopLevel,
  } as ExpandedCollectionNode;

  // Merge shared collections with rewritten paths
  for (const [id, collection] of Object.entries(sharedCollectionsById)) {
    if (id === String(ROOT_COLLECTION.id)) {
      continue;
    }

    mergedCollectionsById[id as CollectionId] = {
      ...collection,

      // Rewrite path: Collections > Shared collections > ...
      path: collection.path
        ? [
            COLLECTIONS_TOP_LEVEL_ID,
            SHARED_TENANT_COLLECTIONS_ROOT_ID,
            ...collection.path.filter(
              (pathId) => pathId !== ROOT_COLLECTION.id,
            ),
          ]
        : null,

      // This loop only processes sharedCollectionsById (tenant collections),
      // never baseCollectionsById (Our Analytics collections), so there is no risk
      // of re-parenting "Our Analytics" sub-collections here.
      parent:
        collection.parent?.id === ROOT_COLLECTION.id
          ? sharedSyntheticRoot
          : collection.parent,
    } as ExpandedCollectionNode;
  }

  // Rewrite paths for all base collections (children of Our Analytics / root)
  // so that "Collections" appears at the top of the breadcrumb when navigating
  // into sub-collections under Our Analytics.
  for (const [id, collection] of Object.entries(baseCollectionsById)) {
    if (id === String(ROOT_COLLECTION.id)) {
      continue; // already rewritten above
    }

    const collectionNode = collection as ExpandedCollectionNode;

    if (!collectionNode.path) {
      continue;
    }

    mergedCollectionsById[id as CollectionId] = {
      ...collectionNode,
      // Rewrite path: Collections > Our Analytics > ...
      path: [COLLECTIONS_TOP_LEVEL_ID, ...collectionNode.path],
    } as ExpandedCollectionNode;
  }

  mergedCollectionsById[SHARED_TENANT_COLLECTIONS_ROOT_ID] =
    sharedSyntheticRoot;

  mergedCollectionsById[COLLECTIONS_TOP_LEVEL_ID] = syntheticTopLevel;

  return mergedCollectionsById;
}
